import React, { useContext, useEffect, useRef, useState } from 'react';
import { Avatar, Button, Checkbox, ConfigProvider, Dropdown, Input, Modal, message } from 'antd';
import { LoadingOutlined, MoreOutlined, SendOutlined, SmileOutlined, UserOutlined } from '@ant-design/icons';
import moment from 'moment';
import { useQueryClient } from '@tanstack/react-query';
import {
  COMMENT_MAX_LENGTH,
  EngagementComment,
  EngagementModelType,
  EngagementThreadPage,
} from './constants';
import {
  addCommentApi,
  blockUserApi,
  deleteCommentApi,
  editCommentApi,
  fetchEngagementReplies,
  useSetReaction,
} from './engagementApi';
import { useEngagementTranslations, useEngagementZIndex } from './EngagementTranslationsContext';
import LinkifiedText from './LinkifiedText';
import { ReactionPicker, ReactionPill } from './ReactionBar';

interface ThreadProps {
  modelType: number;
  modelId: string;
  threadKey: unknown[];
  pages: EngagementThreadPage[];
  hasNextPage?: boolean;
  isFetchingNextPage?: boolean;
  fetchNextPage: () => void;
}

// ---------------------------------------------------------------- avatar

const AVATAR_COLORS = ['#1677ff', '#13a8a8', '#52c41a', '#fa8c16', '#eb2f96', '#722ed1', '#2f54eb', '#a0d911'];

/** the author's initials on a color of their own (no photos) */
const AuthorAvatar: React.FC<{ author?: EngagementComment['author']; size: number }> = ({ author, size }) => {
  const initials = (author?.name || '')
    .trim()
    .split(/\s+/)
    .filter(Boolean)
    .slice(0, 2)
    .map((word) => word[0])
    .join('')
    .toUpperCase();
  if (!initials) {
    return <Avatar size={size} icon={<UserOutlined />} className="engagement-avatar" />;
  }
  return (
    <Avatar
      size={size}
      className="engagement-avatar"
      style={{ backgroundColor: AVATAR_COLORS[(author?.id || 0) % AVATAR_COLORS.length], fontSize: size * 0.42 }}
    >
      {initials}
    </Avatar>
  );
};

/** the antd direction, else the document's (b2c sets <html dir>) */
const useIsRtl = () => {
  const { direction } = useContext(ConfigProvider.ConfigContext);
  return direction === 'rtl' || (typeof document !== 'undefined' && document.documentElement.dir === 'rtl');
};

// ---------------------------------------------------------------- composer

/** a rounded "write a comment" box with the send button inside it - Enter sends, Shift+Enter is a new line, Esc cancels */
const Composer: React.FC<{
  placeholder: string;
  initialValue?: string;
  submitLabel: string;
  autoFocus?: boolean;
  avatarSize?: number;
  onSubmit: (body: string) => Promise<void>;
  onCancel?: () => void;
}> = ({ placeholder, initialValue = '', submitLabel, autoFocus, avatarSize, onSubmit, onCancel }) => {
  const t = useEngagementTranslations();
  const isRtl = useIsRtl();
  const inputRef = useRef<any>(null);
  const [value, setValue] = useState(initialValue);
  const [isSaving, setIsSaving] = useState(false);

  useEffect(() => {
    // the caret after a prefilled "@name " / the text being edited
    if (autoFocus) {
      inputRef.current?.focus({ cursor: 'end' });
    }
  }, [autoFocus]);

  const submit = async () => {
    const body = value.trim();
    if (!body || isSaving) return;
    setIsSaving(true);
    try {
      await onSubmit(body);
      setValue('');
    } finally {
      setIsSaving(false);
    }
  };

  return (
    <div className="engagement-composer">
      {avatarSize && <AuthorAvatar size={avatarSize} />}
      <div className="engagement-composer__main">
        <div className="engagement-composer__box">
          <Input.TextArea
            ref={inputRef}
            bordered={false}
            value={value}
            onChange={(e) => setValue(e.target.value)}
            placeholder={placeholder}
            autoSize={{ minRows: 1, maxRows: 6 }}
            maxLength={COMMENT_MAX_LENGTH}
            onKeyDown={(e) => {
              if (e.key === 'Escape' && onCancel) {
                e.preventDefault();
                onCancel();
              }
            }}
            onPressEnter={(e) => {
              if (!e.shiftKey) {
                e.preventDefault();
                submit();
              }
            }}
          />
          <button
            type="button"
            className="engagement-composer__send"
            title={submitLabel}
            aria-label={submitLabel}
            onClick={submit}
            disabled={!value.trim() || isSaving}
          >
            {isSaving ? <LoadingOutlined /> : <SendOutlined style={isRtl ? { transform: 'scaleX(-1)' } : undefined} />}
          </button>
        </div>
        {onCancel && (
          <button type="button" className="engagement-composer__cancel" onClick={onCancel}>
            {t?.cancel}
          </button>
        )}
      </div>
    </div>
  );
};

// ---------------------------------------------------------------- comment

const CommentItem: React.FC<{
  comment: EngagementComment;
  modelType: number;
  modelId: string;
  threadKey: unknown[];
  canComment: boolean;
  canReact: boolean;
  emojis: string[];
  isReply?: boolean;
  /** a reply's "Reply" - opens its parent's reply box, "@name " in it */
  onReply?: (name?: string | null) => void;
}> = ({ comment, modelType, modelId, threadKey, canComment, canReact, emojis, isReply, onReply }) => {
  const t = useEngagementTranslations();
  const zIndex = useEngagementZIndex();
  const queryClient = useQueryClient();
  const setReaction = useSetReaction(threadKey);
  const [isEditing, setIsEditing] = useState(false);
  const [isReplying, setIsReplying] = useState(false);
  const [replyPrefill, setReplyPrefill] = useState('');
  // replies loaded with "show more replies", after the preview the page brought
  const [moreReplies, setMoreReplies] = useState<EngagementComment[]>([]);
  const [nextAfter, setNextAfter] = useState<number | null>(null);
  const [isLoadingReplies, setIsLoadingReplies] = useState(false);

  const refresh = () => queryClient.invalidateQueries(threadKey);
  const failed = () => {
    message.error(t?.error);
  };

  const previewReplies = comment.replies || [];
  const shownReplies = [...previewReplies, ...moreReplies.filter((r) => !previewReplies.some((p) => p.id === r.id))];
  const hiddenReplies = Math.max(comment.reply_count - shownReplies.length, 0);
  const hasThread = !isReply && (shownReplies.length > 0 || hiddenReplies > 0 || isReplying);

  const startReply = (name?: string | null) => {
    setReplyPrefill(name ? `@${name} ` : '');
    setIsReplying(true);
  };

  const loadMoreReplies = async () => {
    setIsLoadingReplies(true);
    try {
      const after = nextAfter ?? shownReplies[shownReplies.length - 1]?.id;
      const res = await fetchEngagementReplies(comment.id, after);
      setMoreReplies((prev) => [...prev, ...(res?.replies || [])]);
      setNextAfter(res?.next_after ?? null);
    } catch (e) {
      failed();
    } finally {
      setIsLoadingReplies(false);
    }
  };

  const confirmDelete = () =>
    Modal.confirm({
      title: comment.can_edit ? t?.deleteConfirm : t?.hideConfirm,
      zIndex: zIndex ? zIndex + 10 : undefined,
      okButtonProps: { danger: true },
      okText: comment.can_edit ? t?.delete : t?.hide,
      cancelText: t?.cancel,
      onOk: async () => {
        try {
          await deleteCommentApi(comment.id);
          refresh();
        } catch (e) {
          failed();
        }
      },
    });

  const confirmBlock = () => {
    let reason = '';
    let hideComments = false;
    Modal.confirm({
      title: (t?.blockConfirm || '').replace('{name}', comment.author.name || ''),
      zIndex: zIndex ? zIndex + 10 : undefined,
      content: (
        <div className="engagement-block-form">
          <Input.TextArea
            placeholder={t?.blockReason}
            maxLength={500}
            autoSize={{ minRows: 2, maxRows: 4 }}
            onChange={(e) => {
              reason = e.target.value;
            }}
          />
          <Checkbox
            onChange={(e) => {
              hideComments = e.target.checked;
            }}
          >
            {t?.blockHideComments}
          </Checkbox>
        </div>
      ),
      okButtonProps: { danger: true },
      okText: t?.blockUser,
      cancelText: t?.cancel,
      onOk: async () => {
        try {
          await blockUserApi(comment.author.id, reason, hideComments);
          message.success(t?.blockedSuccess);
          refresh();
        } catch (e) {
          failed();
        }
      },
    });
  };

  const menuItems = [
    comment.can_edit && { key: 'edit', label: t?.edit, onClick: () => setIsEditing(true) },
    comment.can_delete && {
      key: 'delete',
      label: comment.can_edit ? t?.delete : t?.hide,
      danger: true,
      onClick: confirmDelete,
    },
    comment.can_block_author && { key: 'block', label: t?.blockUser, danger: true, onClick: confirmBlock },
  ].filter(Boolean) as any[];

  const mine = comment.reactions?.mine;
  const react = (emoji: string) =>
    setReaction.mutate(
      { modelType: EngagementModelType.COMMENT, modelId: String(comment.id), emoji: emoji === mine ? null : emoji },
      { onError: failed }
    );

  return (
    <div
      className={`engagement-comment ${isReply ? 'engagement-comment--reply' : ''} ${
        hasThread ? 'engagement-comment--has-thread' : ''
      }`}
    >
      <div className="engagement-comment__row">
        <AuthorAvatar author={comment.author} size={isReply ? 24 : 32} />
        <div className="engagement-comment__main">
          {isEditing ? (
            <Composer
              placeholder={t?.writeComment || ''}
              initialValue={comment.body}
              submitLabel={t?.save || ''}
              autoFocus
              onCancel={() => setIsEditing(false)}
              onSubmit={async (body) => {
                try {
                  await editCommentApi(comment.id, body);
                  setIsEditing(false);
                  refresh();
                } catch (e) {
                  failed();
                }
              }}
            />
          ) : (
            <>
              <div className="engagement-comment__bubble-row">
                <div className="engagement-comment__bubble">
                  <div className="engagement-comment__header">
                    <span className="engagement-comment__author">{comment.author.name || '-'}</span>
                    {comment.author.agency_name && (
                      <span className="engagement-comment__agency">{comment.author.agency_name}</span>
                    )}
                  </div>
                  <LinkifiedText text={comment.body} className="engagement-comment__body" />
                </div>
                <ReactionPill summary={comment.reactions} />
                {menuItems.length > 0 && (
                  <Dropdown
                    menu={{ items: menuItems }}
                    trigger={['click']}
                    placement="bottomRight"
                    overlayStyle={zIndex ? { zIndex } : undefined}
                  >
                    <Button type="text" size="small" shape="circle" className="engagement-comment__menu" icon={<MoreOutlined />} />
                  </Dropdown>
                )}
              </div>

              <div className="engagement-comment__actions">
                <span
                  className="engagement-comment__date"
                  title={moment(comment.date_created).format('DD/MM/YYYY HH:mm')}
                >
                  {moment(comment.date_created).fromNow()}
                </span>
                {canReact && (
                  <ReactionPicker emojis={emojis} selected={mine} onPick={react}>
                    <button
                      type="button"
                      className={`engagement-comment__action ${mine ? 'is-active' : ''}`}
                      title={t?.react}
                      aria-label={t?.react}
                    >
                      {mine ? <span className="engagement-comment__my-reaction">{mine}</span> : <SmileOutlined />}
                    </button>
                  </ReactionPicker>
                )}
                {canComment && (
                  <button
                    type="button"
                    className="engagement-comment__action"
                    onClick={() => (isReply ? onReply?.(comment.author.name) : startReply())}
                  >
                    {t?.reply}
                  </button>
                )}
                {comment.edited && <span className="engagement-comment__edited">{t?.edited}</span>}
              </div>
            </>
          )}
        </div>
      </div>

      {hasThread && (
        <div className="engagement-replies">
          {shownReplies.map((reply) => (
            <CommentItem
              key={reply.id}
              comment={reply}
              modelType={modelType}
              modelId={modelId}
              threadKey={threadKey}
              canComment={canComment}
              canReact={canReact}
              emojis={emojis}
              isReply
              onReply={startReply}
            />
          ))}
          {hiddenReplies > 0 && (
            <div className="engagement-replies__more">
              <Button type="link" size="small" loading={isLoadingReplies} onClick={loadMoreReplies}>
                {(t?.showReplies || '').replace('{count}', String(hiddenReplies))}
              </Button>
            </div>
          )}
          {isReplying && (
            <div className="engagement-replies__composer">
              <Composer
                key={replyPrefill}
                placeholder={t?.writeReply || ''}
                initialValue={replyPrefill}
                submitLabel={t?.send || ''}
                avatarSize={24}
                autoFocus
                onCancel={() => setIsReplying(false)}
                onSubmit={async (body) => {
                  try {
                    await addCommentApi(modelType, modelId, body, comment.id);
                    setIsReplying(false);
                    setMoreReplies([]);
                    setNextAfter(null);
                    refresh();
                  } catch (e) {
                    failed();
                  }
                }}
              />
            </div>
          )}
        </div>
      )}
    </div>
  );
};

// ---------------------------------------------------------------- thread

/** the composer and the comments of an entity, newest first, "load older" pages back */
const CommentsThread: React.FC<ThreadProps> = ({
  modelType,
  modelId,
  threadKey,
  pages,
  hasNextPage,
  isFetchingNextPage,
  fetchNextPage,
}) => {
  const t = useEngagementTranslations();
  const queryClient = useQueryClient();
  const first = pages[0];
  if (!first) return null;
  const { config, permissions } = first;
  const comments = pages.flatMap((page) => page.comments);

  return (
    <div className="engagement-thread">
      {permissions.can_comment ? (
        <Composer
          placeholder={t?.writeComment || ''}
          submitLabel={t?.send || ''}
          avatarSize={32}
          onSubmit={async (body) => {
            try {
              await addCommentApi(modelType, modelId, body);
              queryClient.invalidateQueries(threadKey);
            } catch (e) {
              message.error(t?.error);
            }
          }}
        />
      ) : (
        permissions.is_blocked && <div className="engagement-thread__notice">{t?.blockedNotice}</div>
      )}

      {comments.length === 0 ? (
        <div className="engagement-thread__empty">{t?.noComments}</div>
      ) : (
        <div className="engagement-thread__list">
          {comments.map((comment) => (
            <CommentItem
              key={comment.id}
              comment={comment}
              modelType={modelType}
              modelId={modelId}
              threadKey={threadKey}
              canComment={permissions.can_comment}
              canReact={config.reactions}
              emojis={config.emojis}
            />
          ))}
        </div>
      )}

      {hasNextPage && (
        <Button type="link" size="small" loading={isFetchingNextPage} onClick={() => fetchNextPage()}>
          {t?.loadOlder}
        </Button>
      )}
    </div>
  );
};

export default CommentsThread;
